// Per-row "Last serviced" cell on the Lifts register (Session P, Item 4). Shows the lift's last
// service date and the projected next service due (last serviced + the service interval, the same
// date the PPM generator will schedule), and lets office/admin edit the last-serviced date inline to
// bootstrap the PPM schedule for a migrated lift. Saving PATCHes the asset; the projection
// recalculates live as the date changes. The dedicated edit surface complements the catch-all
// register edit form (there is no per-lift detail page).
import { useMemo, useState } from "react";
import { assets } from "../../../lib/resources";
import type { Asset } from "../../../lib/resources";
import { useAuth } from "../../../lib/auth";
import { projectNextServiceDue, classifyServiceDue } from "../../../lib/serviceSchedule";
import type { ServiceDueTone } from "../../../lib/serviceSchedule";
import { Button } from "../../../components/Button";
import { toneTextClass } from "../../../components/StatusPill";
import { statusEntry } from "../../../lib/statusRegistry";
import { formatDate } from "../../../lib/format";

// Colour + plain-English status for the projected next-due date. The colour is the registry's
// serviceDue tone (red overdue / amber due soon / green scheduled), the same scheme as the
// NextServiceDueCell, so the two register columns read consistently. A scheduled date carries no label.
const DUE_LABEL: Record<ServiceDueTone, string> = { overdue: "Overdue", due_soon: "Due soon", scheduled: "" };

// An ISO timestamp -> the yyyy-mm-dd a <input type="date"> expects (UTC day).
function toDateInputValue(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toISOString().slice(0, 10);
}

// hideProjection suppresses the internal "Next due" projection line (used on the lift detail page,
// where the Service section already shows the authoritative next scheduled service date, so showing
// a second projected date would read as a contradiction).
// detail widens the control to fill its column and drops the inline "Last serviced:" prefix (the
// lift detail page already labels the field with its own section heading, so the prefix would repeat
// it); on the register it stays the compact fixed-width cell with its own inline label.
export function LastServicedButton({ asset, onChanged, hideProjection, detail }: { asset: Asset; onChanged?: () => void; hideProjection?: boolean; detail?: boolean }) {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "office";

  const [value, setValue] = useState<string | null>(asset.lastServicedAt ?? null); // the stored ISO date (or null)
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(() => toDateInputValue(asset.lastServicedAt ?? null));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The projected next service due: while editing, recompute from the draft so it updates live; else
  // from the stored value. Null -> "Not yet generated".
  const nextDue = useMemo(
    () => projectNextServiceDue(editing ? draft || null : value, asset.frequency ?? null),
    [editing, draft, value, asset.frequency],
  );
  // Band the projected date (overdue / due soon / scheduled) for the colour + label. An overdue date
  // is shown, not hidden -- it is the whole point of the field.
  const dueBand = nextDue ? (classifyServiceDue(nextDue) ?? "scheduled") : null;
  const dueMeta = dueBand ? { cls: toneTextClass(statusEntry("serviceDue", dueBand).tone), label: DUE_LABEL[dueBand] } : null;

  async function save() {
    setBusy(true);
    setError(null);
    try {
      const updated = await assets.update(asset.id, { lastServicedAt: draft }); // "" clears it server-side
      setValue(updated.lastServicedAt ?? null);
      setEditing(false);
      onChanged?.();
    } catch {
      setError("Could not save the date. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={detail ? "text-xs" : "w-[136px] text-xs"}>
      <div>
        {!detail && <span className="text-ele-body opacity-70">Last serviced: </span>}
        {value ? <span className="text-ele-strong">{formatDate(value)}</span> : <span className="text-ele-body opacity-60">Not recorded</span>}
      </div>
      {!hideProjection && (
        <div>
          <span className="text-ele-body opacity-70">Next due: </span>
          {nextDue && dueMeta ? (
            <span className={dueMeta.cls}>
              {formatDate(nextDue)}
              {dueMeta.label ? ` (${dueMeta.label})` : ""}
            </span>
          ) : (
            <span className="text-ele-body opacity-60">Not yet generated</span>
          )}
        </div>
      )}

      {canEdit && !editing && (
        <button
          type="button"
          onClick={() => {
            setDraft(toDateInputValue(value));
            setError(null);
            setEditing(true);
          }}
          className="mt-1 rounded-ele border border-ele-purple-accent px-2 py-1 font-menu text-[11px] tracking-wide text-ele-body hover:border-ele-green"
        >
          Edit
        </button>
      )}

      {canEdit && editing && (
        <div className="mt-1 space-y-1">
          <input
            type="date"
            aria-label="Last serviced date"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-2 py-1 text-xs text-ele-strong outline-none focus:border-ele-green"
          />
          <div className="flex flex-wrap gap-1">
            <Button size="sm" onClick={save} disabled={busy}>
              {busy ? "Saving..." : "Save"}
            </Button>
            <button
              type="button"
              onClick={() => setDraft("")}
              disabled={busy}
              className="rounded-ele border border-ele-purple-accent px-2 py-1 font-menu text-[11px] tracking-wide text-ele-body hover:border-ele-green disabled:opacity-50"
            >
              Clear
            </button>
            <button
              type="button"
              onClick={() => {
                setEditing(false);
                setError(null);
              }}
              disabled={busy}
              className="rounded-ele border border-ele-purple-accent px-2 py-1 font-menu text-[11px] tracking-wide text-ele-body hover:border-ele-green disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
          {error && (
            <p role="alert" className="text-[11px] text-ele-danger">
              {error}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
